import type {
  ApiError,
  AuditableAdapter,
  Enableable,
  Identifiable,
  PivotableAdapter,
  PlatformAuditableEntity,
  PlatformPivotableEntity,
} from '@empathyco/playboard-core-domain'
import type { EqualizeDomainService } from '../../../services'
import type {
  EqualizeAdapter,
  EqualizeBoostingAction,
  EqualizeDTO,
  EqualizeFeaturesDTO,
  EqualizeFieldNamesProvider,
  EqualizeFunctionFieldDTO,
  EqualizeSearchableFieldDTO,
} from '../../equalize'
import {
  AdapterBooleanUtils,
  AdapterDateUtils,
  ConflictApiError,
} from '@empathyco/playboard-core-domain'
import { inject, injectable } from 'inversify'
import { TYPES } from '../../../config/inversify.types'

export interface PlatformEqualizeEntity extends Identifiable, PlatformAuditableEntity {
  endpointId: string
  criteria: PlatformEqualizeCriteria
  title?: string
  notes?: string
  defaultFlag: boolean | string
  features: PlatformEqualizeFeatures
  [key: string]: unknown
}

export interface PlatformEqualizeCriteria extends Enableable, PlatformPivotableEntity {}

export interface PlatformEqualizeFeatures {
  multiMatchSearch?: {
    config: {
      searchableFields: Array<PlatformEqualizeSearchableField>
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  boosting?: {
    config: {
      boosts?: Array<PlatformEqualizeBoostingField>
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  functionScore?: {
    config: {
      functions: Array<PlatformEqualizeFunctionField>
    }
    [key: string]: unknown
  }
}

export interface PlatformEqualizeSearchableField {
  name: string
  boost?: number
}

export interface PlatformEqualizeBoostingField {
  action: EqualizeBoostingAction
  boostFactor: number
  field: string
  value: string
}

export interface PlatformEqualizeFunctionField {
  boostFactor?: number
  fieldName: string
  formulaType: string
  weight?: number
}

export interface PlatformPlayEqualize extends Identifiable {
  enabled: boolean | string
  title: string
  notes?: string
  fields: Array<PlatformPlayEqualizeField>
  metadata: {
    created_on: string
    updated_on: string
    user: string
  }
}

export interface PlatformPlayEqualizeField {
  field: string
  boost: string
}

export interface PlatformEqualizeError {
  status: number
  data?: any
}

@injectable()
export class PlatformEqualizeAdapter
  implements EqualizeAdapter<PlatformEqualizeEntity, PlatformEqualizeError>
{
  public constructor(
    @inject(TYPES.AuditableAdapter)
    protected auditableAdapter: AuditableAdapter<PlatformAuditableEntity>,
    @inject(TYPES.PivotableAdapter)
    protected pivotableAdapter: PivotableAdapter<PlatformPivotableEntity>,
    @inject(TYPES.EqualizeFieldNamesProvider)
    protected equalizeFieldNamesProvider: EqualizeFieldNamesProvider,
    @inject(TYPES.EqualizeDomainService)
    protected domainService: EqualizeDomainService,
  ) {}

  decode(payload: PlatformEqualizeEntity | PlatformPlayEqualize): EqualizeDTO {
    if (payload?.hasOwnProperty('fields')) {
      return this.decodeFromPlay(payload as PlatformPlayEqualize)
    }
    payload = payload as PlatformEqualizeEntity
    const { id, endpointId, criteria, defaultFlag, features, title, ...restOfPayload } =
      payload ?? {}

    const isDefault = AdapterBooleanUtils.toBoolean(defaultFlag)

    return {
      ...restOfPayload,
      id,
      ...this.auditableAdapter.decode(payload),
      endpointId,
      criteria: {
        enabled: isDefault || AdapterBooleanUtils.toBoolean(criteria?.enabled),
        ...this.pivotableAdapter.decode(criteria),
      },
      isDefault,
      title: isDefault ? 'EQ default' : title,
      features: this.decodeFeatures(features),
      defaultFeatures: {} as EqualizeFeaturesDTO,
    }
  }

  private decodeFromPlay(payload: PlatformPlayEqualize): EqualizeDTO {
    const {
      id,
      title,
      notes,
      metadata: { created_on, updated_on, user },
    } = payload
    return {
      id,
      title,
      notes,
      metadata: {
        created: created_on ? AdapterDateUtils.decodeDate(created_on) : undefined,
        updated: updated_on ? AdapterDateUtils.decodeDate(updated_on) : undefined,
        author: user,
      },
      isDefault: false,
      criteria: {
        enabled: AdapterBooleanUtils.toBoolean(payload.enabled),
        pivots: [],
      },
      features: {
        boosting: {
          config: {
            boosts: [],
          },
        },
        functionScore: {
          config: {
            functions: [],
          },
        },
        semanticMatchSearch: {
          config: {
            searchableFields: [],
          },
        },
        multiMatchSearch: {
          config: {
            searchableFields: payload.fields.map(({ field, boost }) => ({
              name: field,
              boost: Number(boost),
            })),
          },
        },
        sponsoredScore: {
          config: {
            functions: [],
          },
        },
      },
      defaultFeatures: {} as EqualizeFeaturesDTO,
    }
  }

  decodeError(error: PlatformEqualizeError): ApiError | ConflictApiError {
    const { status, data } = error
    if (data?.conflictingElements) {
      const conflictingElement = data.conflictingElements[0]
      return new ConflictApiError({
        conflictingElements: [
          {
            id: conflictingElement.id,
            name: conflictingElement.name,
          },
        ],
      })
    } else {
      return {
        status,
      }
    }
  }

  encode(payload: EqualizeDTO): PlatformEqualizeEntity {
    const {
      id,
      endpointId,
      criteria,
      title,
      notes,
      isDefault,
      features,
      defaultFeatures,
      ...restOfPayload
    } = payload

    const pivotsWithValues = criteria.pivots.filter(pivot => pivot.values.length)

    return {
      ...restOfPayload,
      id,
      ...this.auditableAdapter.encode(payload),
      endpointId: endpointId ?? 'search',
      criteria: {
        enabled: criteria.enabled,
        pivots: pivotsWithValues.length ? pivotsWithValues : undefined,
      },
      title,
      notes,
      defaultFlag: isDefault ?? false,
      features: this.encodeFeatures(features, defaultFeatures),
    }
  }

  private decodeFeatures(features: PlatformEqualizeFeatures): EqualizeFeaturesDTO {
    const { multiMatchSearch, boosting, functionScore } = features ?? {}
    return {
      multiMatchSearch: {
        ...multiMatchSearch,
        config: {
          ...multiMatchSearch?.config,
          ...this.decodeSearchableFields(multiMatchSearch?.config),
        },
      },
      semanticMatchSearch: {
        config: this.decodeSearchableFields(multiMatchSearch?.config, true),
      },
      boosting: {
        ...boosting,
        config: {
          ...boosting?.config,
          boosts: boosting?.config.boosts ?? [],
        },
      },
      functionScore: {
        ...functionScore,
        config: this.decodeFunctions(functionScore?.config),
      },
      sponsoredScore: {
        config: this.decodeFunctions(functionScore?.config, true),
      },
    }
  }

  private decodeSearchableFields(
    config: { searchableFields: Array<PlatformEqualizeSearchableField> } | undefined,
    semanticMatchSearch = false,
  ): { searchableFields: Array<EqualizeSearchableFieldDTO> } {
    const semanticFieldNames = this.equalizeFieldNamesProvider().semanticFieldNames

    return {
      searchableFields: (config?.searchableFields ?? []).reduce(
        (fields: Array<EqualizeSearchableFieldDTO>, field) => {
          if (
            (semanticMatchSearch && semanticFieldNames.includes(field.name)) ||
            (!semanticMatchSearch && !semanticFieldNames.includes(field.name))
          ) {
            fields.push(this.decodeSearchableField(field))
          }
          return fields
        },
        [],
      ),
    }
  }

  private decodeSearchableField(
    searchableField: PlatformEqualizeSearchableField,
  ): EqualizeSearchableFieldDTO {
    const { name, boost } = searchableField
    return {
      name,
      boost: boost ?? 0,
    }
  }

  private decodeFunctions(
    config: { functions: Array<PlatformEqualizeFunctionField> } | undefined,
    sponsored = false,
  ): { functions: Array<EqualizeFunctionFieldDTO> } {
    const sponsoredFieldNames = this.equalizeFieldNamesProvider().sponsoredFieldNames

    return {
      functions: (config?.functions ?? []).reduce(
        (functions: Array<EqualizeFunctionFieldDTO>, functionValue) => {
          if (
            (sponsored && sponsoredFieldNames.includes(functionValue.fieldName)) ||
            (!sponsored && !sponsoredFieldNames.includes(functionValue.fieldName))
          ) {
            functions.push(this.decodeFunction(functionValue))
          }
          return functions
        },
        [],
      ),
    }
  }

  private decodeFunction(func: PlatformEqualizeFunctionField): EqualizeFunctionFieldDTO {
    const { boostFactor, fieldName, formulaType, weight } = func
    return {
      boostFactor: boostFactor ?? 0,
      fieldName,
      formulaType,
      weight: weight ?? 0,
    }
  }

  private encodeFeatures(
    features: EqualizeFeaturesDTO,
    defaultFeatures: EqualizeFeaturesDTO,
  ): PlatformEqualizeFeatures {
    const featuresToService = this.domainService.getFeaturesToService(features, defaultFeatures)
    const { multiMatchSearch, semanticMatchSearch, functionScore, sponsoredScore } = features
    const {
      multiMatchSearch: multiMatchSearchToService,
      semanticMatchSearch: semanticMatchSearchToService,
      functionScore: functionScoreToService,
      sponsoredScore: sponsoredScoreToService,
      ...restOfFeatures
    } = featuresToService
    return {
      ...((multiMatchSearchToService || semanticMatchSearchToService) && {
        multiMatchSearch: {
          ...multiMatchSearch,
          config: {
            ...multiMatchSearch.config,
            searchableFields: [
              ...multiMatchSearch.config.searchableFields,
              ...semanticMatchSearch.config.searchableFields,
            ],
          },
        },
      }),
      ...((functionScoreToService || sponsoredScoreToService) && {
        functionScore: {
          ...functionScore,
          config: {
            ...functionScore.config,
            functions: [...functionScore.config.functions, ...sponsoredScore.config.functions],
          },
        },
      }),
      ...restOfFeatures,
    }
  }
}
